// @flow
/* global document */
import * as React from 'react';
import url from 'url';
import { connect } from 'react-redux';
import type { FormProps } from 'redux-form';
import { compose, bindActionCreators } from 'redux';

import { withPageMeta } from '@gandi/app-runner/dist/utils/withPageMeta';
import { notifyDanger } from '@gandi/notifications';
import {
  selectIsAuthenticatedUser,
  selectAuthenticatedUserInfos,
} from '@gandi/authentication';
import { actions as formActions, withForm } from 'gandi.react-form';
import { selectCurrent, selectPersonal } from '@gandi/redux-sharing';
import { selectFromConfig } from '@gandi/redux-config';
import { withLogger } from 'gandi.redux-logger';
import { withTranslator } from '@gandi/translate';

import { withRouter } from 'src/helpers/withRouter';

import type { AbuseFormType, Dispatch, FullState, HandleForm } from '../types';
import Abuse, { type InjectedProps, type OwnProps } from './components/Abuse';
import { AbuseFormValidation } from './components/AbuseForm';
import {
  selectAbuseInitialValues,
  selectAbuseSuccess,
  selectAbuseUpdating,
  sendSuccess,
  setAbuseInitialValues,
  submitAbuseForm,
} from './modules';

const selectPublicUrlV5 = selectFromConfig(
  'app.layout.urls.v5_prefix',
  'https://beta.gandi.net'
);

const EMPTY_OBJECT = {};
const mapStateToProps = (
  state: FullState,
  ownProps: {
    ...OwnProps,
    ...$Diff<InjectedProps, {| reduxFormProps: FormProps |}>,
  }
) => {
  const isAuthenticatedUser = selectIsAuthenticatedUser(state);
  const sharingDetails =
    (isAuthenticatedUser && selectCurrent(state)) || EMPTY_OBJECT;
  const personalDetails = isAuthenticatedUser
    ? selectPersonal(state) || EMPTY_OBJECT
    : EMPTY_OBJECT;
  const userInfos = isAuthenticatedUser
    ? selectAuthenticatedUserInfos(state) || EMPTY_OBJECT
    : EMPTY_OBJECT;

  return {
    success: selectAbuseSuccess(state),
    updating: selectAbuseUpdating(state),
    initialValues: selectAbuseInitialValues(state) || {
      category: ownProps.routeParams && ownProps.routeParams.category,
      sub_category: ownProps.routeParams && ownProps.routeParams.topic,
      firstname: personalDetails.firstname,
      lastname: personalDetails.lastname,
      reply_to: personalDetails.email || userInfos.email,
      orgname:
        sharingDetails.type &&
        sharingDetails.type !== 'individual' &&
        sharingDetails.name &&
        !sharingDetails.name.match(/-GANDI$/)
          ? sharingDetails.name
          : '',
      params: {
        recipient: ownProps.translator.__(
          'GANDI SAS, 63-65 boulevard Massena 75013 Paris - FRANCE'
        ),
      },
    },
    isAuthenticatedUser,
    publicUrlV5: selectPublicUrlV5(state),
  };
};

const mapActionsToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      notifyDanger,
      sendSuccess,
      submitAbuseForm,
    },
    dispatch
  );

const reduxFormConfig = {
  form: 'AbuseForm',
  propNamespace: 'reduxFormProps',
  validate: AbuseFormValidation,
  // Allow to refresh the form when actualizing initialValues client-side:
  enableReinitialize: true,
};

const handleForm = ({
  dispatch,
  params,
  req,
  res,
  translator: { __ },
}: HandleForm<Required<InjectedProps>>) => {
  // $FlowFixMe[incompatible-type]
  const data: Partial<AbuseFormType> = req.body || EMPTY_OBJECT;

  const currentUrl = url.format({
    protocol: req.protocol,
    hostname: req.hostname,
    pathname: req.path,
    query: req.query,
  });

  const promises = [
    dispatch(formActions.initialize('AbuseForm', data)), // so we can map errors to fields
    dispatch(setAbuseInitialValues(data)),
    // send data to the API and redirect
    dispatch(
      submitAbuseForm(
        // $FlowFixMe[incompatible-call] body is untyped
        data,
        // $FlowFixMe[incompatible-call] route params are always present when the form is submitted
        params,
        __,
        req,
        res,
        currentUrl
      )
    ),
  ];

  return Promise.all(promises);
};

export default (compose(
  // $FlowFixMe[underconstrained-implicit-instantiation]
  withRouter,
  withLogger('abuse'),
  withPageMeta(({ __ }) => ({
    title: __('Contact our Abuse team - Gandi.net'),
    description: __(
      'Our legal team is available to file an abuse complaint about a service provided by Gandi. All of your reports will be processed within 24 hours.'
    ),
  })),
  withTranslator({ propsNamespace: 'translator' }),
  connect(mapStateToProps, mapActionsToProps),
  withForm({
    ...reduxFormConfig,
    serverHandler: handleForm, // I don't understand when this is used
    onSubmitFail: () => {
      if (typeof document !== 'undefined') {
        const formElt = document.getElementById('AbuseForm');
        if (formElt) {
          formElt.scrollIntoView();
        }
      }
    },
  })
)(Abuse): React.ComponentType<OwnProps>);
